/*
 * Bridges the Frengee pickup app block into the theme's "Shipping / pickup
 * toggle" card.
 *
 * Frengee owns all pickup logic on this store — transfer sources, priority
 * location routing, per-location pickup times and geolocation sorting (see the
 * app's Fulfillment logic settings). None of that is visible to Shopify's
 * native `store_availabilities` in Liquid, so the theme must not compute
 * pickup itself. This script only MOVES and DECORATES what Frengee renders:
 *
 *   1. Relocates Frengee's summary line into the toggle card's pickup panel,
 *      so it reads as one card instead of two stacked blocks.
 *   2. Injects a small Google Maps embed into each store row in Frengee's
 *      drawer, keyed off the address Frengee already printed.
 *
 * No availability, stock or location data is invented here.
 *
 * IMPORTANT: Frengee fetches its data through a Shopify app proxy
 * (/apps/search-api/...). App proxies return 502 on `shopify theme dev`, so
 * this stays stuck on the fallback text locally. Verify on a draft theme
 * preview on the real store domain.
 */
(function () {
  'use strict';

  /* ---------- 1. Move Frengee's summary into the toggle card ---------- */

  // Move the WHOLE app block into the slot, never just its summary.
  //
  // Frengee resolves its live status by querying inside its own block element
  // (block.querySelector('.frengee-pickup__title') and friends). Lifting the
  // summary out on its own leaves those queries pointing at nothing, so the
  // card stays stuck on "Checking pickup availability..." forever even though
  // the proxy responded fine. Moving the block keeps every internal reference
  // intact.
  function mountSummary() {
    var slot = document.querySelector('[data-frengee-pickup-slot]');
    var block = document.querySelector('[data-frengee-pickup-block]');
    if (!slot || !block) return;

    // The drawer and its overlay are position:fixed. Re-parent them to <body>
    // before the block moves, so they can never end up inside a container that
    // creates a containing block for fixed positioning.
    ['.frengee-drawer', '.frengee-overlay'].forEach(function (sel) {
      var el = block.querySelector(sel);
      if (el && el.parentNode !== document.body) {
        document.body.appendChild(el);
      }
    });

    if (block.parentNode !== slot) {
      slot.appendChild(block);
    }

    // Hide the merchant fallback copy once the app block is mounted — from
    // that point Frengee owns this area, including its own loading state.
    var fallback = slot.querySelector('[data-frengee-pickup-fallback]');
    if (fallback) fallback.hidden = true;
  }

  /* ---------- 2. Add a map to each store row in Frengee's drawer ---------- */

  function normalizeName(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /*
   * Coordinates entered in the block setting, as "Name|lat|lng" per line.
   *
   * Needed because Frengee only prints a street address for the location it
   * resolved against, and Shopify's own location data is invisible to Liquid
   * for any location holding no inventory record for the product. Coordinates
   * are the one source that covers every location on every product.
   *
   * Frengee does hold these already, but only sends them to the storefront
   * when its Geolocation setting is on — which also triggers a browser
   * location prompt. Reading them from the theme setting avoids that.
   */
  var coordCache = null;
  function coordLookup() {
    if (coordCache) return coordCache;
    coordCache = {};

    var slot = document.querySelector('[data-frengee-pickup-slot]');
    var raw = slot && slot.dataset.locationCoords;
    if (!raw) return coordCache;

    raw.split(/[\r\n]+/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 3) return;

      var name = normalizeName(parts[0]);
      var lat = parseFloat(parts[1]);
      var lng = parseFloat(parts[2]);
      if (!name || isNaN(lat) || isNaN(lng)) return;

      // Optional 4th field: the address to display, with " / " between lines.
      var addressLines = [];
      if (parts[3]) {
        addressLines = parts[3]
          .split('/')
          .map(function (part) { return part.trim(); })
          .filter(Boolean);
      }

      coordCache[name] = { coords: lat + ',' + lng, addressLines: addressLines };
    });

    return coordCache;
  }

  // Frengee exposes a normalized key on the row; fall back to the visible name.
  function entryFor(store) {
    var lookup = coordLookup();

    var key = store.dataset.frengeeStoreName || store.dataset.frengeeStoreKey;
    if (key && lookup[normalizeName(key)]) return lookup[normalizeName(key)];

    var name = store.querySelector('.frengee-store__name');
    if (name && lookup[normalizeName(name.textContent)]) {
      return lookup[normalizeName(name.textContent)];
    }

    return null;
  }

  function coordsFor(store) {
    var entry = entryFor(store);
    return entry ? entry.coords : '';
  }

  /*
   * Frengee only prints a street address for the location it resolved against,
   * so sibling locations render without one — Santa Cruz shows an address and
   * Reno doesn't, on the same product. This fills the gap from the block
   * setting so every location reads the same way.
   *
   * Only ever ADDS an address where none exists; an address Frengee supplied
   * is never overwritten.
   */
  function addAddress(store) {
    if (store.querySelector('.frengee-store__address')) return;
    if (store.classList.contains('frengee-store--pickup-disabled')) return;

    var entry = entryFor(store);
    if (!entry || !entry.addressLines.length) return;

    var address = document.createElement('div');
    address.className = 'frengee-store__address';

    entry.addressLines.forEach(function (line, index) {
      if (index) address.appendChild(document.createElement('br'));
      address.appendChild(document.createTextNode(line));
    });

    var status = store.querySelector('.frengee-store__status');
    if (status && status.nextSibling) {
      store.insertBefore(address, status.nextSibling);
    } else {
      store.appendChild(address);
    }
  }

  function addressFor(store) {
    var address = store.querySelector('.frengee-store__address');
    if (!address) return '';

    var text = address.textContent
      // Frengee concatenates address lines without separators; add commas so
      // the geocoder reads "…StreetSanta Cruz" as two parts.
      .replace(/([a-z0-9])([A-Z])/g, '$1, $2')
      .replace(/\s+/g, ' ')
      .trim();

    // Strip a trailing phone number if the app appended one.
    text = text.replace(/,?\s*\+?\d[\d\s()-]{7,}$/, '').trim();

    return text.length > 8 ? text : '';
  }

  function addMap(store) {
    if (store.dataset.themeMapDone === '1') return;
    if (!mapsEnabled()) return;

    // Locations with pickup switched off in Frengee are listed for reference
    // only — no map, per design.
    if (store.classList.contains('frengee-store--pickup-disabled')) return;

    // Coordinates first: they're exact and available for every location.
    // Address is the fallback when no coordinates have been entered.
    var query = coordsFor(store) || addressFor(store);
    if (!query) return;

    store.dataset.themeMapDone = '1';

    var wrap = document.createElement('div');
    wrap.className = 'frengee-store__map';

    var frame = document.createElement('iframe');
    frame.src =
      'https://maps.google.com/maps?q=' +
      encodeURIComponent(query) +
      '&z=14&output=embed';
    frame.title = 'Map of ' + query;
    frame.loading = 'lazy';
    frame.referrerPolicy = 'no-referrer-when-downgrade';
    frame.setAttribute('allowfullscreen', '');
    wrap.appendChild(frame);

    var link = document.createElement('a');
    link.className = 'link frengee-store__directions';
    link.href =
      'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(query);
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = directionsLabel();

    store.appendChild(wrap);
    store.appendChild(link);
  }

  /* ---------- 3. Clarify transfer-based availability ---------- */

  /*
   * A location can read "Out of stock" and still be available for pickup,
   * because Frengee is configured to transfer the item in from another
   * location (Fulfillment logic → transfer sources). Shown as-is, the red
   * "Out of stock" badge and the green "Pickup available" check look like they
   * contradict each other.
   *
   * This only rewords that one case. It does not decide availability — that
   * stays entirely with Frengee. Locations that are genuinely stocked, or
   * genuinely unavailable, are left untouched.
   */
  // Labels this file adds on top of the app's own output. Set in the theme
  // editor on the "Shipping / pickup toggle" block, under "Store list drawer".
  function slotSetting(name, fallback) {
    var slot = document.querySelector('[data-frengee-pickup-slot]');
    var value = slot && slot.dataset[name];
    return value ? value : fallback;
  }

  function transferPrefix() {
    return slotSetting('transferPrefix', 'Available by transfer');
  }

  function directionsLabel() {
    return slotSetting('directionsLabel', 'Get directions');
  }

  function mapsEnabled() {
    return slotSetting('showMaps', 'true') !== 'false';
  }

  function outOfStockLabel() {
    var block = document.querySelector('[data-frengee-pickup-block]');
    return (block && block.dataset.outOfStockLabel) || 'Out of stock';
  }

  // True when Frengee says pickup IS possible but the location holds no stock.
  function isTransferOnly(store) {
    if (!store) return false;

    var badge = store.querySelector('.frengee-badge');
    if (!badge) return false;
    if (badge.textContent.trim() !== outOfStockLabel()) return false;

    // Frengee draws a check for available, a cross for unavailable.
    return !!store.querySelector('.frengee-store__check');
  }

  // "Pickup available, usually ready in 2-4 days" -> "usually ready in 2-4 days"
  function leadTimeFrom(text) {
    var idx = text.indexOf(',');
    return idx === -1 ? '' : text.slice(idx + 1).trim();
  }

  function clarifyStore(store) {
    var statusText = store.querySelector('.frengee-store__status-text');
    if (!statusText) return;

    var prefix = transferPrefix();
    var current = statusText.textContent.trim();
    if (current.indexOf(prefix) === 0) return; // already done
    if (!isTransferOnly(store)) return;

    var lead = leadTimeFrom(current);
    statusText.textContent = lead ? prefix + ' — ' + lead : prefix;
  }

  // The card summary describes one location; match it back to its row in the
  // drawer to find out whether that location is transfer-only.
  function clarifySummary() {
    var slot = document.querySelector('[data-frengee-pickup-slot]');
    if (!slot) return;

    var title = slot.querySelector('.frengee-pickup__title');
    var subtext = slot.querySelector('.frengee-pickup__subtext');
    if (!title || !subtext) return;

    var prefix = transferPrefix();
    var current = subtext.textContent.trim();
    if (!current || current.indexOf(prefix) === 0) return;

    var titleText = title.textContent.trim();
    var match = null;

    document.querySelectorAll('.frengee-store').forEach(function (store) {
      var name = store.querySelector('.frengee-store__name');
      if (!name) return;
      var n = name.textContent.trim();
      if (n && titleText.indexOf(n) !== -1) match = store;
    });

    if (!isTransferOnly(match)) return;

    subtext.textContent = prefix + ' — ' + current.charAt(0).toLowerCase() + current.slice(1);
  }

  function decorateStores() {
    document.querySelectorAll('.frengee-store').forEach(function (store) {
      addAddress(store); // before addMap — the map is appended after it
      addMap(store);
      clarifyStore(store);
    });
    clarifySummary();
  }

  /* ---------- run ---------- */

  function run() {
    mountSummary();
    decorateStores();
  }

  // setTimeout rather than requestAnimationFrame: rAF is paused in background
  // tabs, which would leave the summary unmounted until the tab is focused.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      run();
    }, 0);
  }

  schedule();

  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
